import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import type { Employee, PtoRequest } from "@/types/db";
import { fmtDay } from "@/lib/utils";
import { plainError } from "@/lib/plain-error";

export function PtoAdmin() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<PtoRequest[]>([]);
  const [emps, setEmps] = useState<Employee[]>([]);
  const [statusFilter, setStatusFilter] = useState<"all" | PtoRequest["request_status"]>("pending");

  useEffect(() => { void load(); }, []);
  async function load() {
    const [{ data: r }, { data: e }] = await Promise.all([
      supabase.from("hq_pto_requests").select("*").order("created_at", { ascending: false }),
      supabase.from("hq_employees_people").select("*"),
    ]);
    setRows((r as PtoRequest[]) ?? []);
    setEmps((e as Employee[]) ?? []);
  }

  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [denying, setDenying] = useState<{ id: string; reason: string } | null>(null);
  const filtered = statusFilter === "all" ? rows : rows.filter(r => r.request_status === statusFilter);

  async function notifyDecision(requestId: string) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      void fetch("/.netlify/functions/pto-decision-notify", {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ request_id: requestId }),
      }).catch(() => {});
    } catch { /* fire-and-forget */ }
  }

  // HQ-ERROR-001. Both decisions discarded the update's error and then told the
  // employee what had been decided. A denied write meant the request stayed
  // pending while its owner was notified it had been approved — the worst
  // version of this bug, because the notification is what people act on.
  // Nothing is announced now until the decision is actually recorded.
  // HQ-PTO-001. Two things beyond the discarded error above. The update matched
  // on id alone, so a second click — or a second manager, on the same request,
  // at the same moment — decided it again and sent a second notification, and
  // the last writer won without either of them knowing there had been a race.
  // Requiring the row to still be pending makes the decision happen once: the
  // second attempt changes nothing and says so.
  async function decide(r: PtoRequest, patch: Record<string, unknown>) {
    if (!employee || deciding) return;
    setDecisionError(null);
    setDeciding(r.id);
    const { data, error } = await supabase.from("hq_pto_requests").update({
      approved_by: employee.id,
      approved_at: new Date().toISOString(),
      ...patch,
    }).eq("id", r.id).eq("request_status", "pending").select("id");
    setDeciding(null);
    if (error) {
      setDecisionError(plainError(error, "record that decision"));
      return;
    }
    if (!data || data.length === 0) {
      // Nothing was pending to decide. Say so and reload rather than notify.
      setDecisionError("That request was already decided — someone else got there first. Reloading.");
      await load();
      return;
    }
    void notifyDecision(r.id);
    await load();
  }

  async function approve(r: PtoRequest) {
    await decide(r, { request_status: "approved", denial_reason: null });
  }

  // HQ-NATIVE-DIALOG-001. This was a window.prompt(), which cannot be styled,
  // cannot be reached by keyboard the way the rest of the page can, and blocks
  // the tab while it is open.
  async function confirmDeny(r: PtoRequest, reason: string) {
    setDenying(null);
    await decide(r, { request_status: "denied", denial_reason: reason.trim() || null });
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Time Off Requests</h1>

      {decisionError && (
        <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{decisionError}</div>
      )}

      <div className="flex flex-wrap gap-2">
        {(["pending","approved","denied","cancelled","all"] as const).map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`btn btn-sm ${statusFilter === s ? "" : "btn-ghost"}`}
          >{s} {s !== "all" && `(${rows.filter(r => r.request_status === s).length})`}</button>
        ))}
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-gray2">
              <th className="py-2">Employee</th><th>Dates</th><th>Days</th><th>Reason</th><th>Status</th><th>Notes</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              const e = empById[r.employee_id];
              const days = Math.round((new Date(r.end_date).getTime() - new Date(r.start_date).getTime()) / 86400000) + 1;
              return (
                <tr key={r.id} className="border-b border-line/70">
                  <td className="py-2">{[e?.first_name, e?.last_name].filter(Boolean).join(" ") || e?.email}</td>
                  <td className="py-2">{fmtDay(r.start_date)} – {fmtDay(r.end_date)}</td>
                  <td className="py-2 text-center">{days}</td>
                  <td className="py-2 text-gray2">{r.reason}</td>
                  <td className="py-2">
                    <span className={`pill ${
                      r.request_status === "approved" ? "pill-ok" :
                      r.request_status === "denied" ? "pill-bad" :
                      r.request_status === "cancelled" ? "" : "pill-warn"
                    }`}>{r.request_status}</span>
                  </td>
                  <td className="py-2 text-gray2 max-w-xs">
                    {r.notes && <div>{r.notes}</div>}
                    {r.denial_reason && <div className="text-bad text-xs">Denied: {r.denial_reason}</div>}
                  </td>
                  <td className="py-2 text-right">
                    {r.request_status === "pending" && (
                      denying?.id === r.id ? (
                        <div className="flex items-center justify-end gap-1">
                          <label className="sr-only" htmlFor={`deny-reason-${r.id}`}>Reason for denying</label>
                          <input
                            id={`deny-reason-${r.id}`}
                            className="input w-48 py-1 text-xs"
                            autoFocus
                            placeholder="Reason (optional)"
                            value={denying.reason}
                            onChange={e => setDenying({ id: r.id, reason: e.target.value })}
                            onKeyDown={e => {
                              if (e.key === "Enter") void confirmDeny(r, denying.reason);
                              if (e.key === "Escape") setDenying(null);
                            }}
                          />
                          <button onClick={() => void confirmDeny(r, denying.reason)} disabled={deciding === r.id}
                            className="btn btn-sm btn-ghost text-bad ring-bad">Confirm deny</button>
                          <button onClick={() => setDenying(null)} className="btn btn-sm btn-ghost">Cancel</button>
                        </div>
                      ) : (
                        <>
                          <button onClick={() => void approve(r)} disabled={deciding !== null} className="btn btn-sm">
                            {deciding === r.id ? "Saving…" : "Approve"}
                          </button>
                          <button onClick={() => setDenying({ id: r.id, reason: "" })} disabled={deciding !== null}
                            className="btn btn-sm btn-ghost text-bad ring-bad ml-1">Deny</button>
                        </>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-sm text-gray2">No requests.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
